import React from 'react';
import Select from 'react-select';
import { Grid3x3, LayoutGrid, List, Search as SearchIcon } from 'lucide-react';

import { brandSelectTheme } from './reactSelectTheme';

/**
 * The control strip above a list: search, page-specific refinements, sort, and
 * a view toggle.
 *
 * Every part is optional and renders only when configured. Most pages want
 * search alone; a register with categories adds a select; only pages that have
 * a genuine second layout offer a view toggle. Passing nothing renders
 * nothing — a plain table page should not grow controls it has no use for.
 *
 * Extracted from four hand-rolled copies (Bankhändelser, Inkorg, Tillgångar,
 * Utlägg) whose markup was identical apart from class names and had already
 * started to drift.
 */

const SELECT_STYLES = {
    // 38 = $ll-control-height in _list-toolbar.scss — the one toolbar control
    // height. Change them together.
    control: (base) => ({ ...base, minHeight: 38, fontSize: 13 }),
    option: (base) => ({ ...base, fontSize: 13 }),
    // The toolbar sits inside cards with their own stacking contexts; portal
    // the menu to <body> so it is never clipped.
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const VIEW_ICONS = { list: List, grid: Grid3x3, kanban: LayoutGrid };
const VIEW_LABELS = { list: 'Lista', grid: 'Rutnät', kanban: 'Tavla' };

// Density steps, smallest first. Labelled with initials rather than icons:
// three sizes of the same glyph read as decoration, "S M L" reads as a scale.
const DENSITY_STEPS = [
    { value: 'small', label: 'S', title: 'Små kort' },
    { value: 'medium', label: 'M', title: 'Mellan' },
    { value: 'large', label: 'L', title: 'Stora kort' },
];

export const ListToolbar = ({
    // { value, onChange, placeholder } — onChange receives the raw string.
    search,
    // { options: [{value,label}], value, onChange } — onChange receives the value.
    sort,
    // { value, onChange, steps? } — card size for grid layouts. Promoted out of
    // Dokumentarkiv, which was the only page that had it; Inkorg and Tillgångar
    // render cards too and can adopt it. A page whose grid has one size passes
    // nothing. Pass it only while the sized layout is actually showing —
    // Dokumentarkiv hides it in list view, where it controls nothing.
    density,
    // { value, onChange, modes: ['list','grid'] }
    view,
    // Buttons that act on the page rather than narrow it ("Ladda upp",
    // "Uppdatera"). They sit at the right edge, before density and view — the
    // refinements stay on the left with the search they belong to.
    actions,
    // Page-specific refinements (category pickers, date ranges …).
    children,
    className = '',
}) => {
    if (!search && !sort && !view && !density && !actions && !children) return null;

    const hasRight = Boolean(actions || density || (view && view.modes?.length > 1));
    const densitySteps = density?.steps
        ? DENSITY_STEPS.filter((s) => density.steps.includes(s.value))
        : DENSITY_STEPS;

    return (
        <div className={`ll-toolbar ${className}`.trim()}>
            {search && (
                <div className="ll-toolbar__search">
                    <SearchIcon size={15} className="ll-toolbar__search-icon" aria-hidden="true" />
                    <input
                        type="search"
                        className="ll-toolbar__search-input"
                        value={search.value}
                        onChange={(e) => search.onChange(e.target.value)}
                        placeholder={search.placeholder || 'Sök…'}
                        aria-label={search.ariaLabel || search.placeholder || 'Sök'}
                    />
                </div>
            )}

            {children}

            {sort && (
                <div className="ll-toolbar__sort">
                    <Select
                        options={sort.options}
                        value={sort.options.find((o) => o.value === sort.value) || null}
                        onChange={(opt) => sort.onChange(opt?.value)}
                        isSearchable={false}
                        theme={brandSelectTheme}
                        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                        styles={SELECT_STYLES}
                        aria-label={sort.ariaLabel || 'Sortera'}
                    />
                </div>
            )}

            {hasRight && (
                <div className="ll-toolbar__right">
                    {actions}

                    {density && (
                        <div className="ll-view-toggle" role="group" aria-label="Kortstorlek">
                            {densitySteps.map((step) => (
                                <button
                                    key={step.value}
                                    type="button"
                                    className={`ll-view-btn ll-density-btn ${density.value === step.value ? 'active' : ''}`}
                                    onClick={() => density.onChange(step.value)}
                                    title={step.title}
                                    aria-label={step.title}
                                    aria-pressed={density.value === step.value}
                                >
                                    {step.label}
                                </button>
                            ))}
                        </div>
                    )}

                    {view && view.modes?.length > 1 && (
                        <div className="ll-view-toggle" role="group" aria-label="Vy">
                            {view.modes.map((m) => {
                                const Icon = VIEW_ICONS[m] || List;
                                const label = VIEW_LABELS[m] || m;
                                return (
                                    <button
                                        key={m}
                                        type="button"
                                        className={`ll-view-btn ${view.value === m ? 'active' : ''}`}
                                        onClick={() => view.onChange(m)}
                                        title={label}
                                        aria-label={label}
                                        aria-pressed={view.value === m}
                                    >
                                        <Icon size={16} />
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

/**
 * Status chips with live counts — the "what needs me" layer above search.
 *
 * chips: [{ key, label, count?, countless?, icon?, color? }].
 *   `countless` suppresses the badge for chips that open a panel rather than
 *   filter a set. `icon` is a lucide component; use it when the chips name
 *   *kinds* of thing rather than states — a glyph helps tell "Aktiva" from
 *   "Arkiverade" at a glance in a way it wouldn't for "Betalda"/"Obetalda".
 *
 *   `color` is for chips whose colour already carries meaning elsewhere in the
 *   page. It takes two shapes, because "this chip has a colour" and "this
 *   chip has an accent" are different claims:
 *
 *     color: '#e11d48'          identity. Outlined in its colour when idle,
 *                               filled when active. For chips the colour
 *                               *names* — Dokumentarkiv's user-defined tags
 *                               carry the same colour on every card, so a
 *                               monochrome row would break the association.
 *
 *     color: { text, fill }     accent. Neutral when idle, `fill` background
 *                               with `text` lettering when active. For a set
 *                               where each member has a house colour but the
 *                               row would turn into a rainbow if they all
 *                               showed it at once — Dokumentarkiv's twelve
 *                               document categories.
 *
 *   Don't reach for either to brighten up a set of states: if the colour isn't
 *   already meaningful, it's noise competing with the active-chip signal.
 */
const chipStyle = (color, isActive) => {
    if (!color) return undefined;
    if (typeof color === 'string') {
        return isActive
            ? { background: color, borderColor: color, color: '#fff' }
            : { borderColor: color, color };
    }
    // Accent: idle is left to the stylesheet so the row stays calm.
    return isActive
        ? { background: color.fill, borderColor: color.text, color: color.text }
        : undefined;
};

export const FilterChips = ({ chips, value, onChange, className = '' }) => {
    if (!chips?.length) return null;
    return (
        <div className={`ll-chips ${className}`.trim()} role="group" aria-label="Filter">
            {chips.map((chip) => {
                const isActive = chip.key === value;
                const Icon = chip.icon;
                // Inline because the colours are tenant data or per-category
                // constants, not design tokens — there's no stylesheet to put
                // them in.
                const colorStyle = chipStyle(chip.color, isActive);
                return (
                    <button
                        key={chip.key}
                        type="button"
                        // Keyed off the resolved style, not off `color`: an
                        // idle accent chip has no inline colour and should
                        // keep the house hover.
                        className={`ll-chip ${isActive ? 'active' : ''} ${colorStyle ? 'll-chip--colored' : ''}`.replace(/\s+/g, ' ').trim()}
                        onClick={() => onChange(chip.key)}
                        style={colorStyle}
                        aria-pressed={isActive}
                    >
                        {Icon && <Icon size={14} strokeWidth={1.75} className="ll-chip__icon" />}
                        {chip.label}
                        {!chip.countless && chip.count !== undefined && (
                            <span className="ll-chip__count">{chip.count}</span>
                        )}
                    </button>
                );
            })}
        </div>
    );
};

export default ListToolbar;
